import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPlayerId } from "../lib/ids";
import type { CardDraft, JoinRoomResponse, RoomSnapshot, ServerEvent } from "../shared/game";

type RoomConnectionState = {
  playerId: string;
  playerName: string;
  setPlayerName: (name: string) => void;
  roomCodeInput: string;
  setRoomCodeInput: (code: string) => void;
  room: RoomSnapshot | null;
  connectionStatus: "idle" | "connecting" | "connected" | "error";
  error: string | null;
  createRoom: () => Promise<void>;
  joinRoom: () => Promise<void>;
  syncCard: (card: CardDraft) => void;
  setReady: (isReady: boolean) => void;
  setRoundDuration: (durationMs: number) => void;
  setRoundMode: (mode: "timed" | "solo") => void;
  startGame: () => void;
  submitCard: () => void;
  lockSubmission: () => void;
  advancePhase: () => void;
  restartMatch: () => void;
  leaveRoom: () => void;
};

const CLIENT_STORAGE_KEY = "bgj-cards-player-id";
const CLIENT_PLAYER_NAME_KEY = "bgj-cards-player-name";

export function useRoomConnection(): RoomConnectionState {
  const [playerNameState, setPlayerNameState] = useState(() => window.localStorage.getItem(CLIENT_PLAYER_NAME_KEY) ?? "Player 1");
  const [roomCodeInput, setRoomCodeInput] = useState("");
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<RoomConnectionState["connectionStatus"]>("idle");
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  const playerId = useMemo(() => {
    const existing = window.localStorage.getItem(CLIENT_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const nextId = createPlayerId();
    window.localStorage.setItem(CLIENT_STORAGE_KEY, nextId);
    return nextId;
  }, []);

  useEffect(() => {
    return () => {
      socketRef.current?.close();
    };
  }, []);

  const connectToRoom = useCallback(async (roomCode: string) => {
    setConnectionStatus("connecting");
    setError(null);

    const response = await fetch(`/api/rooms/${roomCode}/join`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        playerId,
        playerName: playerNameState,
      }),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    const payload = (await response.json()) as JoinRoomResponse;
    window.history.replaceState({}, "", `/?room=${payload.roomId}`);
    const socket = new WebSocket(`${window.location.origin.replace("http", "ws")}${payload.websocketPath}`);

    socket.onopen = () => {
      setRoom(payload.snapshot);
      setConnectionStatus("connected");
    };

    socket.onmessage = (event) => {
      const parsed = JSON.parse(event.data) as ServerEvent;
      if (parsed.type === "room_state" || parsed.type === "connected") {
        setRoom(parsed.room);
      } else if (parsed.type === "error") {
        setError(parsed.message);
        setConnectionStatus("error");
      }
    };

    socket.onerror = () => {
      setConnectionStatus("error");
      setError("Connection to room failed.");
    };

    socket.onclose = () => {
      setConnectionStatus((current) => (current === "error" ? current : "idle"));
    };

    socketRef.current?.close();
    socketRef.current = socket;
  }, [playerId, playerNameState]);

  const createRoom = useCallback(async () => {
    try {
      const response = await fetch("/api/rooms", { method: "POST" });
      const payload = (await response.json()) as { roomId: string };
      setRoomCodeInput(payload.roomId);
      await connectToRoom(payload.roomId);
    } catch (caught) {
      setConnectionStatus("error");
      setError(caught instanceof Error ? caught.message : "Could not create room.");
    }
  }, [connectToRoom]);

  const joinRoom = useCallback(async () => {
    if (!roomCodeInput.trim()) {
      setError("Enter a room code first.");
      return;
    }

    try {
      await connectToRoom(roomCodeInput.trim().toUpperCase());
    } catch (caught) {
      setConnectionStatus("error");
      setError(caught instanceof Error ? caught.message : "Could not join room.");
    }
  }, [connectToRoom, roomCodeInput]);

  useEffect(() => {
    const roomFromUrl = new URL(window.location.href).searchParams.get("room");
    if (!roomFromUrl || room || connectionStatus === "connecting" || connectionStatus === "connected") {
      return;
    }

    setRoomCodeInput(roomFromUrl.toUpperCase());
    void connectToRoom(roomFromUrl.toUpperCase()).catch((caught) => {
      setConnectionStatus("error");
      setError(caught instanceof Error ? caught.message : "Could not join room.");
    });
  }, [connectToRoom, connectionStatus, room]);

  const syncCard = useCallback((card: CardDraft) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) {
      return;
    }

    socketRef.current.send(JSON.stringify({ type: "update_card", card }));
  }, []);

  const setPlayerName = useCallback((name: string) => {
    setPlayerNameState(name);
    window.localStorage.setItem(CLIENT_PLAYER_NAME_KEY, name);
  }, []);

  const sendAction = useCallback((payload: object) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) {
      return;
    }

    socketRef.current.send(JSON.stringify(payload));
  }, []);

  const leaveRoom = useCallback(() => {
    socketRef.current?.close();
    socketRef.current = null;
    setRoom(null);
    setConnectionStatus("idle");
    setError(null);
    window.history.replaceState({}, "", "/");
  }, []);

  return {
    playerId,
    playerName: playerNameState,
    setPlayerName,
    roomCodeInput,
    setRoomCodeInput,
    room,
    connectionStatus,
    error,
    createRoom,
    joinRoom,
    syncCard,
    setReady: (isReady) => sendAction({ type: "set_ready", isReady }),
    setRoundDuration: (durationMs) => sendAction({ type: "set_round_duration", durationMs }),
    setRoundMode: (mode) => sendAction({ type: "set_round_mode", mode }),
    startGame: () => sendAction({ type: "start_game" }),
    submitCard: () => sendAction({ type: "submit_card" }),
    lockSubmission: () => sendAction({ type: "lock_submission" }),
    advancePhase: () => sendAction({ type: "advance_phase" }),
    restartMatch: () => sendAction({ type: "restart_match" }),
    leaveRoom,
  };
}
